import React, { useEffect, useState } from "react";
import { MessageCircle, Edit3, Trash2, X as XIcon, Save as SaveIcon } from "lucide-react";
import { updateCheckin, deleteCheckin } from "@/lib/api";
import { ALL_SLOTS } from "./constants";
import { fmtTime, toDatetimeLocal } from "./helpers";

export default function CheckinRow({ goalId, c, editing, onEdit, onSaved, onDeleted }) {
    const [note, setNote] = useState(c.note || "");
    const [slot, setSlot] = useState(c.slot || "");
    const [when, setWhen] = useState(toDatetimeLocal(c.created_at));
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        setNote(c.note || "");
        setSlot(c.slot || "");
        setWhen(toDatetimeLocal(c.created_at));
    }, [c, editing]);

    const save = async () => {
        setSaving(true);
        try {
            const payload = { note, slot };
            if (when) payload.created_at = new Date(when).toISOString();
            await updateCheckin(goalId, c.id, payload);
            onSaved?.();
        } finally { setSaving(false); }
    };

    const remove = async () => {
        if (!window.confirm("Delete this check-in?")) return;
        await deleteCheckin(goalId, c.id);
        onDeleted?.();
    };

    return (
        <li data-testid={`checkin-${c.id}`} className="p-3 rounded-2xl bg-white border border-ink-10">
            <div className="flex items-center gap-2 font-ui text-[11px] text-ink-40">
                <MessageCircle size={11} />
                <span data-testid={`checkin-datetime-${c.id}`}>
                    {new Date(c.created_at).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" })}
                </span>
                {c.slot && (
                    <span className="px-1.5 py-0.5 rounded-full bg-sage-paper text-ink-70 capitalize">{c.slot}</span>
                )}
                {c.slot_time && (
                    <span className="px-1.5 py-0.5 rounded-full bg-sage-paper text-ink-70">{fmtTime(c.slot_time)}</span>
                )}
                {c.passage_served && (
                    <span className="ml-auto text-moss-deep font-medium">{c.passage_served}</span>
                )}
                <button
                    data-testid={`checkin-edit-${c.id}`}
                    onClick={onEdit}
                    className={"ml-auto w-7 h-7 rounded-full flex items-center justify-center transition " + (c.passage_served ? "" : "ml-auto ") + " text-ink-40 hover:text-ink hover:bg-ink-10"}
                    aria-label="Edit check-in"
                    title="Edit check-in"
                >
                    <Edit3 size={12} />
                </button>
                <button
                    data-testid={`checkin-delete-${c.id}`}
                    onClick={remove}
                    className="w-7 h-7 rounded-full flex items-center justify-center text-ink-40 hover:text-plum hover:bg-plum/10 transition"
                    aria-label="Delete check-in"
                    title="Delete check-in"
                >
                    <Trash2 size={12} />
                </button>
            </div>
            {!editing && c.note && (
                <p className="mt-1 scripture text-[15px] text-ink leading-snug">{c.note}</p>
            )}
            {editing && (
                <div className="mt-3 space-y-2">
                    <label className="block font-ui text-[10px] uppercase tracking-[0.2em] text-ink-40">When</label>
                    <input
                        data-testid={`edit-when-${c.id}`}
                        type="datetime-local"
                        value={when}
                        onChange={e => setWhen(e.target.value)}
                        className="w-full px-3 py-2 rounded-xl bg-white border border-ink-10 font-ui text-[13px] focus:outline-none focus:border-moss/60"
                    />
                    <label className="block font-ui text-[10px] uppercase tracking-[0.2em] text-ink-40 mt-2">Slot</label>
                    <div className="flex flex-wrap gap-1.5">
                        {[{ key: "", label: "None" }, ...ALL_SLOTS].map(s => (
                            <button
                                key={s.key || "none"}
                                onClick={() => setSlot(s.key)}
                                className={
                                    "px-2.5 py-1 rounded-full font-ui text-[11px] transition " +
                                    (slot === s.key ? "bg-moss text-white" : "bg-sage-paper text-ink-70 hover:bg-ink-10")
                                }
                            >
                                {s.label}
                            </button>
                        ))}
                    </div>
                    <label className="block font-ui text-[10px] uppercase tracking-[0.2em] text-ink-40 mt-2">Note</label>
                    <textarea
                        data-testid={`edit-note-${c.id}`}
                        value={note}
                        onChange={e => setNote(e.target.value)}
                        rows={2}
                        className="w-full px-3 py-2 rounded-xl bg-white border border-ink-10 scripture text-[14px] focus:outline-none focus:border-moss/60 resize-none"
                    />
                    <div className="flex justify-end gap-2 pt-1">
                        <button
                            onClick={onEdit}
                            className="px-3 py-1.5 rounded-full font-ui text-[12px] text-ink-70 hover:bg-ink-10 inline-flex items-center gap-1"
                        >
                            <XIcon size={12} /> Cancel
                        </button>
                        <button
                            data-testid={`edit-save-${c.id}`}
                            onClick={save}
                            disabled={saving}
                            className="px-3 py-1.5 rounded-full bg-moss text-white font-ui text-[12px] hover:bg-moss-deep inline-flex items-center gap-1"
                        >
                            <SaveIcon size={12} /> Save
                        </button>
                    </div>
                </div>
            )}
        </li>
    );
}
